import React, { useCallback, useEffect, useMemo, useState, useContext } from 'react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { RefreshCw, Clock, BarChart2, AlertTriangle, ExternalLink } from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceArea,
} from 'recharts';
import { adsAgeApi } from 'api';
import { useAuth } from 'components/context/AuthContext';
import { ModalsContext } from 'components/context/ModalsContext';
import { useAds } from 'components/context/AdsContext';
import { formatCurrencyAmount } from 'utils/currency';
import EsporaiTooltip from 'components/ui/EsporaiTooltip';

const HowItWorksContent = () => (
  <div className="w-full text-white">
    <div className="space-y-2">
      <p className="text-sm font-semibold text-white/95">¿Qué mide este número?</p>
      <p className="text-xs text-white/80 leading-relaxed">
        Es la <span className="text-white/95 font-semibold">edad promedio ponderada por gasto</span> de tus anuncios activos
        en Meta.
      </p>
    </div>

    <div className="h-px bg-white/10 my-3" />

    <div className="space-y-2">
      <p className="text-sm font-semibold text-white/95">Cómo se calcula</p>
      <ol className="text-xs text-white/80 leading-relaxed list-decimal pl-5 space-y-1">
        <li>Toma los anuncios activos.</li>
        <li>Calcula cuántos días lleva activo cada anuncio (edad).</li>
        <li>Pondera esa edad por el % de gasto de cada anuncio.</li>
      </ol>
      <p className="text-xs text-white/75 leading-relaxed">
        El resultado es un solo valor que resume si tu presupuesto está yendo a creatividades frescas o antiguas.
      </p>
    </div>

    <div className="h-px bg-white/10 my-3" />

    <div className="space-y-2">
      <p className="text-sm font-semibold text-white/95">Ejemplo</p>
      <div className="rounded-xl border border-white/10 bg-white/5 p-3">
        <div className="text-xs text-white/85 space-y-1">
          <div>Ad A: 5 días, 60% gasto</div>
          <div>Ad B: 30 días, 30% gasto</div>
          <div>Ad C: 45 días, 10% gasto</div>
        </div>
        <div className="text-xs text-white/85 mt-2">
          (5×0.60) + (30×0.30) + (45×0.10) = <span className="text-white font-semibold">16.5 días</span>
        </div>
      </div>
      <p className="text-xs text-white/75 leading-relaxed">
        En este caso, el gasto de la cuenta se está concentrando en creatividades de ~16.5 días.
      </p>
    </div>

    <div className="h-px bg-white/10 my-3" />

    <div className="space-y-2">
      <p className="text-sm font-semibold text-white/95">Cómo interpretarlo</p>
      <div className="grid grid-cols-1 gap-2">
        <div className="rounded-xl border border-emerald-400/20 bg-emerald-500/10 p-3">
          <div className="text-sm font-semibold text-emerald-200">0–30 días</div>
          <div className="text-xs text-white/80">Saludable: el gasto se distribuye en anuncios frescos.</div>
        </div>
        <div className="rounded-xl border border-amber-400/20 bg-amber-500/10 p-3">
          <div className="text-sm font-semibold text-amber-200">30–50 días</div>
          <div className="text-xs text-white/80">Conviene renovar anuncios para sostener resultados.</div>
        </div>
        <div className="rounded-xl border border-rose-400/20 bg-rose-500/10 p-3">
          <div className="text-sm font-semibold text-rose-200">50+ días</div>
          <div className="text-xs text-white/80">
            El presupuesto se está destinando a anuncios antiguos: es necesario renovar para seguir mejorando.
          </div>
        </div>
      </div>
      <p className="text-xs text-white/75 leading-relaxed">
        Si lanzaste anuncios nuevos pero el número sigue alto, suele significar que esos anuncios nuevos no están recibiendo
        gasto (no se están testeando o no están funcionando).
      </p>
      <p className="text-xs text-white/60 leading-relaxed">
        Nota: si la compra de medios está muy desordenada, esta métrica puede volverse menos representativa.
      </p>
    </div>
  </div>
);

const CARD_CLASS =
  'backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-2xl shadow-lg';

const fmtShortDate = (yyyyMmDd) => {
  try {
    const d = parseISO(yyyyMmDd);
    return format(d, 'dd/MM', { locale: es });
  } catch {
    return yyyyMmDd;
  }
};

const fmtLongDate = (yyyyMmDd) => {
  try {
    const d = parseISO(yyyyMmDd);
    return format(d, "d 'de' MMMM, yyyy", { locale: es });
  } catch {
    return yyyyMmDd;
  }
};

const formatNumber = (value, { maximumFractionDigits = 2 } = {}) => {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('es-AR', { maximumFractionDigits }).format(n);
};

const formatPct = (value) => {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${new Intl.NumberFormat('es-AR', { maximumFractionDigits: 2 }).format(n)}%`;
};

const toFiniteNumberOrNull = (value) => {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const formatConversions = (value) => {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(Math.round(n));
};

const formatRoas = (value) => {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)}x`;
};

const SkeletonBlock = ({ className }) => (
  <div className={`animate-pulse bg-white/10 rounded-xl ${className || ''}`} />
);

const EmptyState = ({ title, description, action }) => (
  <div className={`${CARD_CLASS} p-6`}>
    <div className="flex items-start gap-3">
      <div className="rounded-xl bg-amber-500/15 border border-amber-400/25 p-2">
        <AlertTriangle className="w-5 h-5 text-amber-300" />
      </div>
      <div className="min-w-0">
        <h2 className="text-white font-semibold">{title}</h2>
        {description ? <p className="text-sm text-white/70 mt-1">{description}</p> : null}
        {action ? <div className="mt-4">{action}</div> : null}
      </div>
    </div>
  </div>
);

const AdsAgeReport = () => {
  const { auth, hasMetaConnected } = useAuth();
  const { openConnectionsModal } = useContext(ModalsContext);
  const { activeMetaAccount } = useAds();
  const brandId = auth?.user?.active_brand_id;

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const [sort, setSort] = useState({ key: 'budget_pct', dir: 'desc' });

  const load = useCallback(async () => {
    if (!brandId) {
      setPayload(null);
      setLoading(false);
      setErrorMessage('No hay marca activa seleccionada.');
      return;
    }

    setLoading(true);
    setErrorMessage('');
    try {
      const res = await adsAgeApi.getAdsAge(brandId);
      setPayload(res.data);
    } catch (err) {
      const apiError = err?.response?.data?.error;
      setPayload(null);
      setErrorMessage(apiError || err.message || 'No se pudo cargar el reporte.');
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    load();
  }, [load]);

  const chartData = useMemo(() => {
    const dates = payload?.dates || [];
    const values = payload?.values || [];
    return dates.map((d, i) => ({
      date: d,
      label: fmtShortDate(d),
      value: values[i] ?? null,
    }));
  }, [payload]);

  const yMax = useMemo(() => {
    const nums = chartData.map((d) => (typeof d.value === 'number' ? d.value : null)).filter((v) => v !== null);
    const max = nums.length ? Math.max(...nums) : 0;
    const padded = Math.ceil((max || 0) / 10) * 10;
    return Math.max(60, padded || 0);
  }, [chartData]);

  const detail = payload?.detail || null;
  const latestDate = detail?.date || (payload?.dates || []).slice(-1)[0] || null;
  const latestWeightedAge = detail?.weighted_avg_age ?? null;
  const latestAds = useMemo(() => (Array.isArray(detail?.ads) ? detail.ads : []), [detail]);

  const activeAdsCount = detail ? latestAds.length : null;
  const spendTotal = detail
    ? latestAds.reduce((acc, a) => acc + (Number(a?.spend) || 0), 0)
    : null;

  const metaActId = useMemo(() => {
    const activeBrand = auth?.user?.brands?.find((b) => b.id === auth?.user?.active_brand_id) || auth?.user?.active_brand || null;
    const raw = activeBrand?.meta_ad_account_id || auth?.user?.meta_ad_account_id || null;
    if (raw === null || raw === undefined) return null;
    const cleaned = String(raw).replace(/^act_/, '').trim();
    return cleaned || null;
  }, [auth?.user?.active_brand_id, auth?.user?.active_brand, auth?.user?.brands, auth?.user?.meta_ad_account_id]);

  const displayCurrency = useMemo(() => {
    // Prefer the active Meta account currency when available, since spend values come from Meta.
    const fromMetaAccount = activeMetaAccount?.currency_code || activeMetaAccount?.currency || null;
    if (fromMetaAccount) return String(fromMetaAccount).trim().toUpperCase();

    // Fallback to user fields if present (some endpoints attach display_currency).
    const fromUser = auth?.user?.display_currency || auth?.user?.currency || null;
    if (fromUser) return String(fromUser).trim().toUpperCase();

    return 'USD';
  }, [activeMetaAccount?.currency, activeMetaAccount?.currency_code, auth?.user?.currency, auth?.user?.display_currency]);

  const formatMoney = useCallback((value) => {
    return formatCurrencyAmount(value, displayCurrency, {
      locale: 'es-UY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      display: 'code',
    });
  }, [displayCurrency]);

  const buildMetaDeepLink = useCallback((actId, adExternalId, adName) => {
    const base = 'https://adsmanager.facebook.com/adsmanager/manage/ads';
    const columns =
      'name,delivery,results,cost_per_result,budget,spend,impressions,reach,frequency,cpm,actions:omni_purchase,purchase_roas:omni_purchase,attribution_setting';

    const act = String(actId || '').trim();
    const adId = String(adExternalId || '').trim();
    if (!act || !adId) return null;

    const params = new URLSearchParams();
    params.set('act', act);
    params.set('selected_ad_ids', adId);
    params.set('columns', columns);
    params.set('attribution_windows', 'default');

    const name = String(adName || '').trim();
    if (name) {
      // Construir filter_set con el nombre del anuncio
      const escaped = `[\\\"${name}\\\"]`;
      const filterRaw = `SEARCH_BY_ADGROUP_NAME-STRING\x1ECONTAINS_ALL\x1E"${escaped}"`;
      params.set('filter_set', filterRaw);
    }

    return `${base}?${params.toString()}`;
  }, []);

  const openAdsManager = useCallback((adExternalId, adName) => {
    const url = buildMetaDeepLink(metaActId, adExternalId, adName);
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [buildMetaDeepLink, metaActId]);

  const sortedAds = useMemo(() => {
    if (!detail) return [];
    const rows = latestAds.map((ad, idx) => ({
      ad,
      idx,
      budget_pct: toFiniteNumberOrNull(ad?.budget_pct),
      age_days: toFiniteNumberOrNull(ad?.age_days),
      spend: toFiniteNumberOrNull(ad?.spend),
    }));

    const { key, dir } = sort || {};
    const sign = dir === 'asc' ? 1 : -1;
    const getVal = (r) => r?.[key] ?? null;

    rows.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);

      const aMissing = va === null;
      const bMissing = vb === null;
      if (aMissing && bMissing) return a.idx - b.idx;
      if (aMissing) return 1;
      if (bMissing) return -1;

      if (va < vb) return -1 * sign;
      if (va > vb) return 1 * sign;
      return a.idx - b.idx;
    });

    return rows.map((r) => r.ad);
  }, [detail, latestAds, sort]);

  const toggleSort = useCallback((key) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'desc' };
      return { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' };
    });
  }, []);

  const ariaSortFor = (key) => {
    if (sort?.key !== key) return 'none';
    return sort?.dir === 'asc' ? 'ascending' : 'descending';
  };

  const showMetaConnectHint = !hasMetaConnected;

  return (
    <div className="min-h-full bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 p-4 sm:p-6">
      <div className="max-w-none mx-auto space-y-4 sm:space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-white/80">
              <Clock className="w-5 h-5 text-fuchsia-300" />
              <span className="text-sm font-semibold tracking-wide uppercase">Meta</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3 mt-1">
              <h1 className="text-white text-2xl sm:text-3xl font-extrabold tracking-tight">
                Edad de anuncios
              </h1>
              <EsporaiTooltip
                content={<HowItWorksContent />}
                position="bottom"
                align="start"
                openOn="click"
                showOverlay
                showCloseButton
                scrollable
                scrollClassName="max-h-[70vh] overflow-y-auto pr-2"
                contentClassName="p-4 pr-11 rounded-2xl border border-white/15 bg-gradient-to-br from-slate-800/80 via-slate-900/80 to-indigo-900/80 text-white shadow-2xl backdrop-blur-xl max-w-[min(920px,calc(100vw-32px))] w-[min(920px,calc(100vw-32px))]"
              >
                <button
                  type="button"
                  className="mt-2 sm:mt-0 inline-flex items-center gap-2 rounded-full bg-amber-500/20 border border-amber-400/30 hover:bg-amber-500/30 hover:border-amber-400/50 transition-all duration-200 px-3 py-1.5 text-xs sm:text-sm font-semibold text-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50"
                  aria-label="Cómo funciona este análisis"
                >
                  <span aria-hidden>💡</span>
                  ¿Cómo funciona este análisis?
                </button>
              </EsporaiTooltip>
            </div>
            <p className="text-sm text-white/70 mt-1">
              Serie de 7 días (promedio ponderado) y desglose del último día disponible.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/10 hover:bg-white/15 text-white px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            >
              <RefreshCw className="w-4 h-4" />
              Actualizar
            </button>
          </div>
        </div>

        {showMetaConnectHint ? (
          <EmptyState
            title="Meta no está conectado"
            description="Conectá tu cuenta de Meta Ads para ver esta métrica para la marca activa."
            action={
              <button
                type="button"
                onClick={openConnectionsModal}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-5 py-2.5 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
              >
                Ir a conexiones
                <ExternalLink className="w-4 h-4" />
              </button>
            }
          />
        ) : null}

        {loading ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className={`${CARD_CLASS} p-4`}>
                <SkeletonBlock className="h-4 w-28" />
                <SkeletonBlock className="h-8 w-24 mt-3" />
                <SkeletonBlock className="h-3 w-48 mt-2" />
              </div>
              <div className={`${CARD_CLASS} p-4`}>
                <SkeletonBlock className="h-4 w-32" />
                <SkeletonBlock className="h-8 w-20 mt-3" />
                <SkeletonBlock className="h-3 w-40 mt-2" />
              </div>
              <div className={`${CARD_CLASS} p-4`}>
                <SkeletonBlock className="h-4 w-24" />
                <SkeletonBlock className="h-8 w-28 mt-3" />
                <SkeletonBlock className="h-3 w-44 mt-2" />
              </div>
            </div>
            <div className={`${CARD_CLASS} p-4`}>
              <SkeletonBlock className="h-64 w-full" />
            </div>
            <div className={`${CARD_CLASS} p-4`}>
              <SkeletonBlock className="h-10 w-64" />
              <SkeletonBlock className="h-40 w-full mt-4" />
            </div>
          </div>
        ) : errorMessage ? (
          <EmptyState
            title="No se pudo cargar el reporte"
            description={errorMessage}
            action={
              <button
                type="button"
                onClick={load}
                className="inline-flex items-center gap-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/15 text-white px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
              >
                <RefreshCw className="w-4 h-4" />
                Reintentar
              </button>
            }
          />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className={`${CARD_CLASS} p-4`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-bold text-white/70 uppercase tracking-wider">
                    Edad promedio (último día)
                  </p>
                  <Clock className="w-4 h-4 text-fuchsia-300" />
                </div>
                <p className="text-3xl font-extrabold text-white mt-2">
                  {formatNumber(latestWeightedAge, { maximumFractionDigits: 1 })}{' '}
                  <span className="text-base font-semibold text-white/70">días</span>
                </p>
                <p className="text-sm text-white/60 mt-1">
                  {latestDate ? `Fecha: ${fmtLongDate(latestDate)}` : '—'}
                </p>
              </div>

              <div className={`${CARD_CLASS} p-4`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-bold text-white/70 uppercase tracking-wider">
                    Anuncios activos
                  </p>
                  <BarChart2 className="w-4 h-4 text-cyan-300" />
                </div>
                <p className="text-3xl font-extrabold text-white mt-2">
                  {activeAdsCount === null ? '—' : formatNumber(activeAdsCount, { maximumFractionDigits: 0 })}
                </p>
              </div>

              <div className={`${CARD_CLASS} p-4`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-bold text-white/70 uppercase tracking-wider">Gasto</p>
                  <div className="rounded-lg bg-emerald-500/15 border border-emerald-400/20 px-2 py-1">
                    <span className="text-[11px] font-semibold text-emerald-200">7 días</span>
                  </div>
                </div>
                <p className="text-3xl font-extrabold text-white mt-2">
                  {spendTotal === null ? '—' : formatMoney(spendTotal)}
                </p>
              </div>
            </div>

            <div className={`${CARD_CLASS} p-4 sm:p-5`}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="min-w-0">
                  <h2 className="text-white font-semibold">Evolución (7 días)</h2>
                  <p className="text-sm text-white/60 mt-0.5">
                    Promedio ponderado de edad (días). Los días sin data se muestran como huecos.
                  </p>
                </div>
              </div>

              {chartData.length === 0 ? (
                <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center">
                  <p className="text-sm text-white/70">No hay datos para mostrar en el período.</p>
                </div>
              ) : (
                <div className="h-64 sm:h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                      {/* Bandas de contexto (edad en días) */}
                      <ReferenceArea y1={0} y2={30} fill="rgba(34, 197, 94, 0.12)" />
                      <ReferenceArea y1={30} y2={50} fill="rgba(245, 158, 11, 0.12)" />
                      <ReferenceArea y1={50} y2={yMax} fill="rgba(239, 68, 68, 0.10)" />
                      <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="3 3" />
                      <XAxis
                        dataKey="label"
                        tick={{ fill: 'rgba(255,255,255,0.75)', fontSize: 12 }}
                        axisLine={{ stroke: 'rgba(255,255,255,0.15)' }}
                        tickLine={{ stroke: 'rgba(255,255,255,0.15)' }}
                      />
                      <YAxis
                        tick={{ fill: 'rgba(255,255,255,0.75)', fontSize: 12 }}
                        axisLine={{ stroke: 'rgba(255,255,255,0.15)' }}
                        tickLine={{ stroke: 'rgba(255,255,255,0.15)' }}
                        width={44}
                        domain={[0, yMax]}
                        tickFormatter={(v) => (v === null || v === undefined ? '' : `${v}`)}
                      />
                      <Tooltip
                        contentStyle={{
                          background: 'rgba(15, 23, 42, 0.9)',
                          border: '1px solid rgba(255,255,255,0.12)',
                          borderRadius: 12,
                        }}
                        labelStyle={{ color: 'rgba(255,255,255,0.9)' }}
                        itemStyle={{ color: 'rgba(255,255,255,0.9)' }}
                        formatter={(value) => {
                          if (value === null || value === undefined) return ['Sin data', 'Edad'];
                          return [`${formatNumber(value, { maximumFractionDigits: 1 })} días`, 'Edad'];
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="url(#ageGradient)"
                        strokeWidth={3}
                        dot={{ r: 3, strokeWidth: 0, fill: '#a78bfa' }}
                        activeDot={{ r: 5, fill: '#f0abfc' }}
                        connectNulls={false}
                      />
                      <defs>
                        <linearGradient id="ageGradient" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor="#60a5fa" />
                          <stop offset="50%" stopColor="#a78bfa" />
                          <stop offset="100%" stopColor="#f0abfc" />
                        </linearGradient>
                      </defs>
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
                <div className="min-w-0">
                  <h2 className="text-white font-semibold">Desglose por anuncio</h2>
                  <p className="text-sm text-white/60 mt-0.5">
                    {detail?.date
                      ? `Último día: ${fmtLongDate(detail.date)}`
                      : 'No hay desglose guardado para el último día.'}
                  </p>
                </div>
                <div className="text-sm text-white/70">
                  {detail?.weighted_avg_age === null || detail?.weighted_avg_age === undefined
                    ? null
                    : `Promedio: ${formatNumber(detail.weighted_avg_age, { maximumFractionDigits: 1 })} días`}
                </div>
              </div>

              {!detail ? (
                <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center">
                  <p className="text-sm text-white/70">
                    Todavía no hay desglose disponible. Si el gráfico tiene datos, el desglose puede tardar en almacenarse.
                  </p>
                </div>
              ) : latestAds.length === 0 ? (
                <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center">
                  <p className="text-sm text-white/70">No hay anuncios para mostrar en el desglose.</p>
                </div>
              ) : (
                <>
                  {/* Mobile: cards */}
                  <div className="sm:hidden space-y-3">
                    {sortedAds.map((ad) => (
                      <div key={ad.ad_id} className="rounded-xl border border-white/10 bg-white/5 p-4">
                        <div className="space-y-2">
                          <div className="min-w-0">
                            <div
                              className="text-sm font-semibold text-white truncate"
                              title={ad.ad_name?.trim() || undefined}
                            >
                              {ad.ad_name || '—'}
                            </div>
                            <div className="text-xs text-white/60">ID: {ad.ad_id || '—'}</div>
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs font-semibold text-white/70 uppercase tracking-wider">Campaña</div>
                            <div
                              className="text-sm text-white truncate"
                              title={ad.campaign_name?.trim() || undefined}
                            >
                              {ad.campaign_name || '—'}
                            </div>
                            <div className="text-xs text-white/60">ID: {ad.campaign_id || '—'}</div>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div className="rounded-lg bg-white/5 border border-white/10 p-2">
                              <div className="text-[11px] font-bold text-white/60 uppercase">%</div>
                              <div className="text-sm font-semibold text-white">{formatPct(ad.budget_pct)}</div>
                            </div>
                            <div className="rounded-lg bg-white/5 border border-white/10 p-2">
                              <div className="text-[11px] font-bold text-white/60 uppercase">Días</div>
                              <div className="text-sm font-semibold text-white">
                                {formatNumber(ad.age_days, { maximumFractionDigits: 0 })}
                              </div>
                            </div>
                            <div className="rounded-lg bg-white/5 border border-white/10 p-2">
                              <div className="text-[11px] font-bold text-white/60 uppercase">Gasto</div>
                              <div className="text-sm font-semibold text-white">
                                {ad.spend === null || ad.spend === undefined ? '—' : formatMoney(ad.spend)}
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div className="rounded-lg bg-white/5 border border-white/10 p-2 min-w-0">
                              <div className="text-[11px] font-bold text-white/60 uppercase tracking-wide truncate">
                                Conversiones
                              </div>
                              <div className="text-sm font-semibold text-white">{formatConversions(ad.conversions)}</div>
                            </div>
                            <div className="rounded-lg bg-white/5 border border-white/10 p-2">
                              <div className="text-[11px] font-bold text-white/60 uppercase">ROAS</div>
                              <div className="text-sm font-semibold text-white">{formatRoas(ad.roas)}</div>
                            </div>
                            <div className="rounded-lg bg-white/5 border border-white/10 p-2 min-w-0">
                              <div className="text-[11px] font-bold text-white/60 uppercase">CPA</div>
                              <div className="text-sm font-semibold text-white truncate">
                                {toFiniteNumberOrNull(ad?.cpa) === null ? '—' : formatMoney(ad.cpa)}
                              </div>
                            </div>
                          </div>
                          <div className="pt-1">
                            <button
                              type="button"
                              onClick={() => openAdsManager(ad.ad_id, ad.ad_name)}
                              disabled={!metaActId || !ad.ad_id}
                              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/15 text-white px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                            >
                              Ver en meta
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop/tablet: table */}
                  <div className="hidden sm:block rounded-xl border border-white/10">
                    <table className="min-w-full w-full table-fixed">
                      <thead className="bg-white/5">
                        <tr>
                          <th className="text-left text-xs font-bold uppercase tracking-wider text-white/70 px-3 py-3 w-[22%]">
                            Anuncio
                          </th>
                          <th className="text-left text-xs font-bold uppercase tracking-wider text-white/70 px-3 py-3 w-[22%]">
                            Campaña
                          </th>
                          <th
                            className="text-center text-xs font-bold uppercase tracking-wider text-white/70 px-2 py-3 w-[9%]"
                            aria-sort={ariaSortFor('budget_pct')}
                          >
                            <button
                              type="button"
                              onClick={() => toggleSort('budget_pct')}
                              className="w-full inline-flex items-center justify-center hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 rounded-md"
                              aria-label="Ordenar por porcentaje de inversión"
                            >
                              % inversión
                            </button>
                          </th>
                          <th
                            className="text-center text-xs font-bold uppercase tracking-wider text-white/70 px-2 py-3 w-[8%]"
                            aria-sort={ariaSortFor('age_days')}
                          >
                            <button
                              type="button"
                              onClick={() => toggleSort('age_days')}
                              className="w-full inline-flex items-center justify-center hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 rounded-md"
                              aria-label="Ordenar por días activo"
                            >
                              Días activo
                            </button>
                          </th>
                          <th
                            className="text-center text-xs font-bold uppercase tracking-wider text-white/70 px-2 py-3 w-[10%]"
                            aria-sort={ariaSortFor('spend')}
                          >
                            <button
                              type="button"
                              onClick={() => toggleSort('spend')}
                              className="w-full inline-flex items-center justify-center hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 rounded-md"
                              aria-label="Ordenar por gasto"
                            >
                              Gasto
                            </button>
                          </th>
                          <th className="text-center text-xs font-bold uppercase tracking-wider text-white/70 px-2 py-3 w-[10%]">
                            Conversiones
                          </th>
                          <th className="text-center text-xs font-bold uppercase tracking-wider text-white/70 px-2 py-3 w-[7%]">
                            ROAS
                          </th>
                          <th className="text-center text-xs font-bold uppercase tracking-wider text-white/70 px-2 py-3 w-[9%]">
                            CPA
                          </th>
                          <th className="text-center text-xs font-bold uppercase tracking-wider text-white/70 px-2 py-3 w-[100px]">
                            Acción
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedAds.map((ad) => (
                          <tr key={ad.ad_id} className="border-t border-white/10 hover:bg-white/5 transition-colors">
                            <td className="px-3 py-3">
                              <div className="min-w-0">
                                <div
                                  className="text-sm font-semibold text-white truncate"
                                  title={ad.ad_name?.trim() || undefined}
                                >
                                  {ad.ad_name || '—'}
                                </div>
                                <div className="text-xs text-white/60 truncate" title={ad.ad_id ? `ID: ${ad.ad_id}` : undefined}>
                                  ID: {ad.ad_id || '—'}
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-3">
                              <div className="min-w-0">
                                <div
                                  className="text-sm font-semibold text-white truncate"
                                  title={ad.campaign_name?.trim() || undefined}
                                >
                                  {ad.campaign_name || '—'}
                                </div>
                                <div
                                  className="text-xs text-white/60 truncate"
                                  title={ad.campaign_id ? `ID: ${ad.campaign_id}` : undefined}
                                >
                                  ID: {ad.campaign_id || '—'}
                                </div>
                              </div>
                            </td>
                            <td className="px-2 py-3 text-center text-xs font-semibold text-white whitespace-nowrap">
                              {formatPct(ad.budget_pct)}
                            </td>
                            <td className="px-2 py-3 text-center text-xs font-semibold text-white whitespace-nowrap">
                              {formatNumber(ad.age_days, { maximumFractionDigits: 0 })}
                            </td>
                            <td className="px-2 py-3 text-center text-xs font-semibold text-white whitespace-nowrap">
                              {ad.spend === null || ad.spend === undefined ? '—' : formatMoney(ad.spend)}
                            </td>
                            <td className="px-2 py-3 text-center text-xs font-semibold text-white whitespace-nowrap">
                              {formatConversions(ad.conversions)}
                            </td>
                            <td className="px-2 py-3 text-center text-xs font-semibold text-white whitespace-nowrap">
                              {formatRoas(ad.roas)}
                            </td>
                            <td className="px-2 py-3 text-center text-xs font-semibold text-white whitespace-nowrap">
                              {toFiniteNumberOrNull(ad?.cpa) === null ? '—' : formatMoney(ad.cpa)}
                            </td>
                            <td className="px-2 py-3 text-center align-middle">
                              <button
                                type="button"
                                onClick={() => openAdsManager(ad.ad_id, ad.ad_name)}
                                disabled={!metaActId || !ad.ad_id}
                                className="inline-flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-white px-2 py-1.5 text-[11px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 whitespace-nowrap"
                              >
                                Ver en meta
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdsAgeReport;

